import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createChart } from 'lightweight-charts';
import priceTicker from './priceTicker';
import CandlePanel from './CandlePanel';
import { useTranslation } from 'react-i18next';
import './CandlestickChart.css';
import './WeeklyAverageYearChart.css';

const PRECIO_OFICIAL = 7.50;

const SPANISH_MONTHS_SHORT = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

const TIMEFRAME_LABELS = {
  '30m': '30M',
  '1h':  '1H',
  '1d':  '1D',
  '1w':  '1W',
  '1M':  '1M',
};

// ISO week number from a UNIX timestamp (seconds)
const isoWeekFromTimestamp = (ts) => {
  const d = new Date(ts * 1000);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 3 - ((d.getDay() + 6) % 7));
  const week1 = new Date(d.getFullYear(), 0, 4);
  return 1 + Math.round(((d - week1) / 86400000 - 3 + ((week1.getDay() + 6) % 7)) / 7);
};

// Format X-axis label depending on the active timeframe
const tickLabelForTimeframe = (timeframe, time) => {
  const d = new Date(time * 1000);
  if (timeframe === '1w') return `Wk${isoWeekFromTimestamp(time)}`;
  if (timeframe === '1M') return `${SPANISH_MONTHS_SHORT[d.getMonth()]} ${String(d.getFullYear()).slice(2)}`;
  if (timeframe === '1d') return `${d.getDate()} ${SPANISH_MONTHS_SHORT[d.getMonth()]}`;
  // 30m, 1h — show HH:MM (with date when crossing midnight, lightweight-charts handles that via tick density)
  const h = String(d.getHours()).padStart(2, '0');
  const m = String(d.getMinutes()).padStart(2, '0');
  return `${h}:${m}`;
};

// Long-form date label for tooltips
const longDateForTimeframe = (timeframe, time) => {
  const d = new Date(time * 1000);
  const day = d.getDate();
  const mon = SPANISH_MONTHS_SHORT[d.getMonth()];
  const yr  = d.getFullYear();
  if (timeframe === '1w') {
    return `Wk${isoWeekFromTimestamp(time)} · semana del ${day} ${mon} ${yr}`;
  }
  if (timeframe === '1M') {
    return `${SPANISH_MONTHS_SHORT[d.getMonth()]} ${yr}`;
  }
  if (timeframe === '1d') {
    return `${day} ${mon} ${yr}`;
  }
  // 30m / 1h — date + time
  const h = String(d.getHours()).padStart(2, '0');
  const m = String(d.getMinutes()).padStart(2, '0');
  return `${day} ${mon} ${yr} · ${h}:${m}`;
};

// Period stats from candle data: open (first), close (last), high (max), low (min), change %
const computeCandleStats = (data) => {
  if (!data?.length) return null;
  const first = data[0];
  const last  = data[data.length - 1];
  let high = -Infinity, low = Infinity, highCandle = first, lowCandle = first;
  for (const c of data) {
    if (c.high > high) { high = c.high; highCandle = c; }
    if (c.low  < low)  { low  = c.low;  lowCandle  = c; }
  }
  const change    = last.close - first.open;
  const changePct = first.open !== 0 ? (change / first.open) * 100 : 0;
  return {
    open: first.open,
    close: last.close,
    high,
    low,
    highCandle,
    lowCandle,
    change,
    changePct,
  };
};

const CandlestickChart = () => {
  const { t } = useTranslation();
  const containerRef      = useRef(null);
  const chartRef          = useRef(null);
  const candleSeriesRef   = useRef(null);
  const volumeSeriesRef   = useRef(null);
  const tooltipRef        = useRef(null);
  const [isLoading, setIsLoading]       = useState(true);
  const [error, setError]               = useState(null);
  const [timeframe, setTimeframe]       = useState('1d');
  const [zone, setZone]                 = useState('norte');
  const [isLive, setIsLive]             = useState(false);
  const [lastUpdate, setLastUpdate]     = useState(null);
  const [stats, setStats]               = useState(null); // {open, close, high, low, change, changePct, highCandle, lowCandle}
  const availableTimeframes             = priceTicker.getAvailableTimeframes();
  const currentPrice                    = stats?.close ?? null;

  const isBoth = zone === 'both';

  // ─── Inicializar gráfico — siempre, independiente del estado de carga ──────
  useEffect(() => {
    if (!containerRef.current || isBoth) return;

    const chart = createChart(containerRef.current, {
      width:  containerRef.current.clientWidth,
      height: 480,
      layout: {
        background: { color: '#131722' },
        textColor:  '#9ba5bc',
        fontSize:   12,
        fontFamily: "'Inter', system-ui, sans-serif",
      },
      grid: {
        vertLines: { color: 'rgba(42,46,57,0.6)', style: 1 },
        horzLines: { color: 'rgba(42,46,57,0.6)', style: 1 },
      },
      crosshair: {
        mode: 1,
        vertLine: { color: '#4a5568', width: 1, style: 3, labelBackgroundColor: '#1e2a3a' },
        horzLine: { color: '#4a5568', width: 1, style: 3, labelBackgroundColor: '#1e2a3a' },
      },
      rightPriceScale: {
        borderColor: 'rgba(42,46,57,0.8)',
        scaleMargins: { top: 0.1, bottom: 0.1 },
      },
      timeScale: {
        borderColor:              'rgba(42,46,57,0.8)',
        timeVisible:              timeframe === '30m' || timeframe === '1h',
        secondsVisible:           false,
        rightOffset:              12,
        barSpacing:               8,
        lockVisibleTimeRangeOnResize: true,
        rightBarStaysOnScroll:    true,
        tickMarkFormatter:        (time) => tickLabelForTimeframe(timeframe, time),
      },
    });

    const candleSeries = chart.addCandlestickSeries({
      upColor:        '#17c671',
      downColor:      '#f53d6b',
      borderUpColor:  '#17c671',
      borderDownColor:'#f53d6b',
      wickUpColor:    '#17c671',
      wickDownColor:  '#f53d6b',
      priceFormat: { type: 'price', precision: 2, minMove: 0.01 },
    });

    // Reservar 24% inferior para el histograma de volumen
    candleSeries.priceScale().applyOptions({
      scaleMargins: { top: 0.1, bottom: 0.26 },
    });

    // Línea de referencia: precio oficial $7.50
    candleSeries.createPriceLine({
      price: PRECIO_OFICIAL,
      color: 'rgba(155,165,188,0.6)',
      lineWidth: 1,
      lineStyle: 2, // dashed
      axisLabelVisible: true,
      title: 'Oficial',
    });

    // Histograma de volumen (cantidad de reportes por vela)
    const volumeSeries = chart.addHistogramSeries({
      priceFormat: { type: 'volume' },
      priceScaleId: '',
      lastValueVisible: false,
      priceLineVisible: false,
    });
    volumeSeries.priceScale().applyOptions({
      scaleMargins: { top: 0.78, bottom: 0 },
    });

    chartRef.current        = chart;
    candleSeriesRef.current = candleSeries;
    volumeSeriesRef.current = volumeSeries;

    // Tooltip OHLC custom siguiendo el crosshair
    chart.subscribeCrosshairMove(param => {
      const tip = tooltipRef.current;
      const container = containerRef.current;
      if (!tip || !container) return;
      const w = container.clientWidth;
      const h = container.clientHeight;
      if (!param.time || !param.point || param.point.x < 0 || param.point.y < 0 || param.point.x > w || param.point.y > h) {
        tip.style.display = 'none';
        return;
      }
      const data = param.seriesData.get(candleSeries);
      if (!data || data.open == null) { tip.style.display = 'none'; return; }
      const dateLabel = longDateForTimeframe(timeframe, data.time);
      const change    = data.close - data.open;
      const changePct = data.open !== 0 ? (change / data.open) * 100 : 0;
      const changeColor = change >= 0 ? '#17c671' : '#f53d6b';
      const changeArrow = change >= 0 ? '▲' : '▼';
      tip.innerHTML = `
        <div class="cs-tip__date">${dateLabel}</div>
        <div class="cs-tip__row"><span class="cs-tip__label">Apertura</span><span class="cs-tip__value">$${data.open.toFixed(2)}</span></div>
        <div class="cs-tip__row"><span class="cs-tip__label">Máximo</span><span class="cs-tip__value">$${data.high.toFixed(2)}</span></div>
        <div class="cs-tip__row"><span class="cs-tip__label">Mínimo</span><span class="cs-tip__value">$${data.low.toFixed(2)}</span></div>
        <div class="cs-tip__row"><span class="cs-tip__label">Cierre</span><span class="cs-tip__value">$${data.close.toFixed(2)}</span></div>
        <div class="cs-tip__row" style="border-top:1px solid rgba(155,165,188,0.18); margin-top:4px; padding-top:6px">
          <span class="cs-tip__label">Variación</span>
          <span class="cs-tip__value" style="color:${changeColor}">${changeArrow} ${Math.abs(changePct).toFixed(2)}%</span>
        </div>
      `;
      tip.style.display = 'block';
      const tipW = tip.clientWidth;
      const tipH = tip.clientHeight;
      let x = param.point.x + 16;
      let y = param.point.y - tipH - 16;
      if (x + tipW > w) x = param.point.x - tipW - 16;
      if (y < 0) y = param.point.y + 16;
      tip.style.left = `${x}px`;
      tip.style.top  = `${y}px`;
    });

    const handleResize = () => {
      if (containerRef.current) {
        chart.applyOptions({ width: containerRef.current.clientWidth });
      }
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      chartRef.current        = null;
      candleSeriesRef.current = null;
      volumeSeriesRef.current = null;
      chart.remove();
    };
  }, [timeframe, isBoth]);

  // ─── Conectar ticker — después de que el gráfico esté listo ──────────────────
  useEffect(() => {
    if (isBoth) return undefined; // En modo Ambas usamos CandlePanel directos
    let unsubscribe;
    let cancelled = false;

    const connect = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const applyHistory = (data) => {
          if (!data?.length) return;
          candleSeriesRef.current.setData(data);
          chartRef.current.timeScale().fitContent();
          const s = computeCandleStats(data);
          setStats(s);
          // Marcadores Máx / Mín del período visible
          if (s) {
            candleSeriesRef.current.setMarkers([
              { time: s.highCandle.time, position: 'aboveBar', color: '#17c671', shape: 'arrowDown', text: `Máx $${s.high.toFixed(2)}` },
              { time: s.lowCandle.time,  position: 'belowBar', color: '#f53d6b', shape: 'arrowUp',   text: `Mín $${s.low.toFixed(2)}`  },
            ]);
          }
          // Volumen: una barra por vela coloreada según subió/bajó
          if (volumeSeriesRef.current) {
            volumeSeriesRef.current.setData(data.map(c => ({
              time:  c.time,
              value: c.volume || 1,
              color: c.close >= c.open ? 'rgba(23,198,113,0.45)' : 'rgba(245,61,107,0.45)',
            })));
          }
        };

        unsubscribe = priceTicker.subscribe((update) => {
          if (cancelled) return;
          if (!candleSeriesRef.current || !chartRef.current) return;
          try {
            if (update.type === 'history') {
              applyHistory(update.data || []);
              setIsLive(true);
              setLastUpdate(new Date());
              setIsLoading(false);
            } else if (update.type === 'update') {
              if (update.data) {
                candleSeriesRef.current.update(update.data);
                // Actualizar el cierre en stats sin recalcular máx/mín
                setStats(prev => prev ? { ...prev, close: update.data.close } : prev);
                setIsLive(true);
                setLastUpdate(new Date());
              }
            } else if (update.type === 'timeframe_change') {
              const history = update.history || [];
              if (history.length > 0) {
                applyHistory(history);
                if (update.currentCandle) {
                  candleSeriesRef.current.update(update.currentCandle);
                }
              }
              setIsLive(true);
              setLastUpdate(new Date());
            }
          } catch (e) {
            console.error('Error procesando update:', e);
          }
        });

        await priceTicker.start(timeframe, zone);

        // Si después de iniciar sigue cargando, forzar off
        if (!cancelled) setIsLoading(false);

      } catch (e) {
        if (!cancelled) {
          setError('Error al conectar con el mercado. Verifica tu conexión.');
          setIsLive(false);
          setIsLoading(false);
        }
      }
    };

    // Pequeño delay para que el efecto del gráfico termine primero
    const timer = setTimeout(connect, 50);

    return () => {
      cancelled = true;
      clearTimeout(timer);
      if (unsubscribe) unsubscribe();
    };
  }, [timeframe, zone, isBoth]);

  // ─── Detener ticker al desmontar ─────────────────────────────────────────────
  useEffect(() => {
    return () => priceTicker.stop();
  }, []);

  // ─── Detector de desconexión (sin updates por >5 min) ───────────────────────
  useEffect(() => {
    if (!isLive || !lastUpdate) return;
    const check = setInterval(() => {
      if (Date.now() - lastUpdate > 5 * 60 * 1000) setIsLive(false);
    }, 60000);
    return () => clearInterval(check);
  }, [isLive, lastUpdate]);

  // ─── Cambio de timeframe ─────────────────────────────────────────────────────
  const handleTimeframeChange = useCallback(async (tf) => {
    if (tf === timeframe) return;
    setIsLoading(true);
    setTimeframe(tf);
    try {
      await priceTicker.changeTimeframe(tf);
    } catch (e) {
      setError('Error al cambiar intervalo');
    }
    setIsLoading(false);
  }, [timeframe]);

  const handleZoneChange = useCallback(async (z) => {
    if (z === zone) return;
    // Si vamos a 'both', detener priceTicker (no soporta multi-zona).
    // Si volvemos de 'both' a una zona simple, reiniciar priceTicker.
    if (z === 'both') {
      priceTicker.stop();
      setIsLive(false);
      setZone(z);
      return;
    }
    setIsLoading(true);
    setZone(z);
    try {
      if (zone === 'both') {
        // Reiniciar el ticker con la nueva zona
        await priceTicker.start(timeframe, z);
      } else {
        await priceTicker.changeZone(z);
      }
    } catch (e) {
      setError('Error al cambiar zona');
    }
    setIsLoading(false);
  }, [zone, timeframe]);

  // ─── Quick-zoom de rango visible ─────────────────────────────────────────────
  const handleZoom = useCallback((days) => {
    if (!chartRef.current) return;
    if (days === 'all') {
      chartRef.current.timeScale().fitContent();
      return;
    }
    const now  = Math.floor(Date.now() / 1000);
    const from = now - days * 86400;
    chartRef.current.timeScale().setVisibleRange({ from, to: now });
  }, []);

  return (
    <div className="cs-wrapper">
      {/* Header */}
      <div className="cs-header">
        <div className="cs-title-group">
          <span className="cs-title">Mercado en Vivo — Precio del Banano</span>
          <div className="zone-tabs" style={{ marginTop: 4 }}>
            <button className={`zone-tab ${zone === 'norte' ? 'zone-tab--active' : ''}`} onClick={() => handleZoneChange('norte')} disabled={isLoading}>{t('zona_norte')}</button>
            <button className={`zone-tab ${zone === 'sur'   ? 'zone-tab--active' : ''}`} onClick={() => handleZoneChange('sur')}   disabled={isLoading}>{t('zona_sur')}</button>
            <button className={`zone-tab ${isBoth ? 'zone-tab--active' : ''}`} onClick={() => handleZoneChange('both')} disabled={isLoading}>Ambas</button>
          </div>
          {!isLoading && !error && !isBoth && (
            <div className={`cs-live-badge ${isLive ? 'cs-live-badge--on' : 'cs-live-badge--off'}`}>
              <span className="cs-live-dot" />
              {isLive ? 'EN VIVO' : 'DESCONECTADO'}
              {isLive && lastUpdate && (
                <span className="cs-live-time">
                  &nbsp;• {lastUpdate.toLocaleTimeString('es-EC', { hour: '2-digit', minute: '2-digit' })}
                </span>
              )}
            </div>
          )}
          {isBoth && (
            <div className="cs-live-badge cs-live-badge--info">
              <span className="cs-live-dot" />
              COMPARANDO ZONAS
            </div>
          )}
        </div>

        {!isBoth && stats && !isLoading && (
          <div className="cs-stats">
            <div className="cs-stat">
              <span className="cs-stat__label">USD/caja</span>
              <span className="cs-stat__value cs-stat__value--big">${stats.close.toFixed(2)}</span>
            </div>
            <div className="cs-stat">
              <span className="cs-stat__label">Variación</span>
              <span className={`cs-stat__value ${stats.change >= 0 ? 'cs-stat__value--up' : 'cs-stat__value--down'}`}>
                {stats.change >= 0 ? '▲' : '▼'} {Math.abs(stats.changePct).toFixed(2)}%
              </span>
            </div>
            <div className="cs-stat">
              <span className="cs-stat__label">Máx período</span>
              <span className="cs-stat__value">${stats.high.toFixed(2)}</span>
            </div>
            <div className="cs-stat">
              <span className="cs-stat__label">Mín período</span>
              <span className="cs-stat__value">${stats.low.toFixed(2)}</span>
            </div>
          </div>
        )}
      </div>

      {/* Selector de timeframe + zoom rápido */}
      <div className="cs-tf-bar">
        <div className="cs-tf-group">
          <span className="cs-tf-group__label">Vela</span>
          {availableTimeframes.map(tf => (
            <button
              key={tf.value}
              className={`cs-tf-btn ${timeframe === tf.value ? 'cs-tf-btn--active' : ''}`}
              onClick={() => handleTimeframeChange(tf.value)}
              disabled={isLoading}
              title={tf.label}
            >
              {TIMEFRAME_LABELS[tf.value] ?? tf.label}
            </button>
          ))}
        </div>
        <div className="cs-tf-group">
          <span className="cs-tf-group__label">Zoom</span>
          <button className="cs-tf-btn" onClick={() => handleZoom(1)}    disabled={isLoading}>24H</button>
          <button className="cs-tf-btn" onClick={() => handleZoom(7)}    disabled={isLoading}>7D</button>
          <button className="cs-tf-btn" onClick={() => handleZoom(30)}   disabled={isLoading}>1M</button>
          <button className="cs-tf-btn" onClick={() => handleZoom('all')} disabled={isLoading}>Todo</button>
        </div>
      </div>

      {/* Leyenda — solo en single zone */}
      {!isBoth && !isLoading && !error && (
        <div className="cs-legend">
          <span className="cs-legend-item cs-legend-up">▲ Subida</span>
          <span className="cs-legend-item cs-legend-down">▼ Bajada</span>
          <span className="cs-legend-info">
            Actualización: cada 20 min &nbsp;·&nbsp; Precio en USD por caja
          </span>
        </div>
      )}

      {/* Single-zone chart */}
      {!isBoth && (
        <div className="cs-chart-wrapper">
          <div ref={containerRef} className="cs-chart" />
          <div ref={tooltipRef} className="cs-tip" />

          {isLoading && (
            <div className="cs-overlay">
              <div className="cs-spinner" />
              <p>Cargando datos del mercado...</p>
            </div>
          )}

          {error && !isLoading && (
            <div className="cs-overlay cs-overlay--error">
              <p>{error}</p>
              <button onClick={() => window.location.reload()} className="cs-retry-btn">
                Reintentar
              </button>
            </div>
          )}
        </div>
      )}

      {/* Modo Ambas: dos paneles apilados (Norte arriba, Sur abajo) */}
      {isBoth && (
        <div className="cs-dual">
          <CandlePanel zone="norte" timeframe={timeframe} label="Zona Norte" height={300} />
          <CandlePanel zone="sur"   timeframe={timeframe} label="Zona Sur"   height={300} />
        </div>
      )}

      {/* Footer */}
      <div className="cs-footer">
        <span>Zona horaria UTC · BanaPrecios.com</span>
        <span>
          {isBoth ? (
            <span className="cs-footer-info">● Comparación Norte/Sur · refresco cada 60s</span>
          ) : isLoading ? (
            <span style={{ color: '#9ba5bc' }}>Iniciando...</span>
          ) : isLive ? (
            <span className="cs-footer-live">● En vivo</span>
          ) : (
            <span className="cs-footer-off">● Desconectado</span>
          )}
        </span>
      </div>
    </div>
  );
};

export default CandlestickChart;
